import React, { useEffect, useMemo, useRef } from 'react'
import * as pdfjsLib from 'pdfjs-dist'
import type { PDFDocumentProxy, RenderParameters } from 'pdfjs-dist/types/src/display/api'
import './PDFViewer.component.css'
import { SignatureOverlay } from '../../utilities/pdf.util'
import { ICustomInput } from '../../features/fillablePDFs/store/fillablePDFs.types'
import { PdfRenderStatus } from '../../features/fillablePDFs/store/fillablePDFs.constants'
import Logger from '../../services/logger.service'
import 'pdfjs-dist/build/pdf.worker.min.mjs'

type PDFViewerProps = {
  fileUrl: string | null
  className?: string
  signatureFields?: SignatureOverlay[]
  customInputs?: ICustomInput[]
  onClickSignature?: (elementId: string) => void
  zoomScale?: number
  rerenderOnCustomInputs?: boolean
  onRenderStatusChange?: (status: PdfRenderStatus) => void
}

export const PDFViewer: React.FC<PDFViewerProps> = ({
  fileUrl,
  className,
  signatureFields = [],
  customInputs = [],
  onClickSignature,
  zoomScale = 1,
  rerenderOnCustomInputs = true,
  onRenderStatusChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const onClickSignatureRef = useRef(onClickSignature)
  const prevFileUrlRef = useRef<string | null>(null)
  const prevSignatureFieldsRef = useRef<SignatureOverlay[] | null>(null)
  const prevZoomScaleRef = useRef<number | null>(null)

  useEffect(() => {
    onClickSignatureRef.current = onClickSignature
  }, [onClickSignature])

  const signatureMap = useMemo(() => {
    const map: Record<string, string> = {}

    customInputs.forEach((input) => {
      if (input.fieldType !== 'signature') return

      if (typeof input.answerId === 'string') {
        map[String(input.questionId)] = input.answerId
        return
      }

      const trimmedDataUrl = input.answerId?.trimmedDataUrl
      const originalDataUrl = input.answerId?.originalDataUrl

      if (typeof trimmedDataUrl === 'string') {
        map[String(input.questionId)] = trimmedDataUrl
        return
      }

      if (typeof originalDataUrl === 'string') {
        map[String(input.questionId)] = originalDataUrl
      }
    })

    return map
  }, [customInputs])

  useEffect(() => {
    const container = containerRef.current
    if (!container || !fileUrl) return

    const fileUrlUnchanged = prevFileUrlRef.current === fileUrl
    const signatureFieldsUnchanged = prevSignatureFieldsRef.current === signatureFields
    const zoomScaleUnchanged = prevZoomScaleRef.current === zoomScale

    if (!rerenderOnCustomInputs && fileUrlUnchanged && signatureFieldsUnchanged && zoomScaleUnchanged) {
      return
    }

    prevFileUrlRef.current = fileUrl
    prevSignatureFieldsRef.current = signatureFields
    prevZoomScaleRef.current = zoomScale

    let cancelled = false
    const renderTasks: Array<{ cancel?: () => void; promise?: Promise<any> }> = []
    let pdf: PDFDocumentProxy | null = null

    onRenderStatusChange?.(PdfRenderStatus.LOADING)
    container.innerHTML = ''

    const run = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument(fileUrl)
        pdf = await loadingTask.promise
        if (cancelled) return

        const containerWidth = container.clientWidth || 390

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) return

          const pageIndex = pageNum - 1
          const page = await pdf.getPage(pageNum)

          const viewportAt1 = page.getViewport({ scale: 1 })
          const fitScale = containerWidth / viewportAt1.width
          const scale = fitScale * zoomScale
          const viewport = page.getViewport({ scale })

          const pageWrap = document.createElement('div')
          pageWrap.className = 'pdf-page-wrap'
          pageWrap.style.width = `${Math.floor(viewport.width)}px`
          container.appendChild(pageWrap)

          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          if (!ctx) continue

          canvas.width = Math.floor(viewport.width)
          canvas.height = Math.floor(viewport.height)
          canvas.className = 'pdf-canvas'
          pageWrap.appendChild(canvas)

          const overlayLayer = document.createElement('div')
          overlayLayer.className = 'pdf-page-overlay'
          pageWrap.appendChild(overlayLayer)

          const sigsOnPage = signatureFields.filter((s) => s.pageIndex === pageIndex)

          for (const sig of sigsOnPage) {
            const [x1, y1, x2, y2] = viewport.convertToViewportRectangle([
              sig.rect.x,
              sig.rect.y,
              sig.rect.x + sig.rect.width,
              sig.rect.y + sig.rect.height,
            ])

            const left = Math.min(x1, x2)
            const top = Math.min(y1, y2)
            const width = Math.abs(x2 - x1)
            const height = Math.abs(y2 - y1)

            const btn = document.createElement('button')
            btn.type = 'button'
            btn.style.left = `${left}px`
            btn.style.top = `${top}px`
            btn.style.width = `${width}px`
            btn.style.height = `${height}px`
            btn.title = sig.label ?? 'Sign'

            const savedSignature = signatureMap[String(sig.elementId)]

            if (savedSignature) {
              btn.className = 'pdf-signature-hitbox pdf-signature-hitbox-transparent'
            } else {
              btn.className = 'pdf-signature-hitbox'
              btn.innerHTML = `
                <span class="pdf-signature-hitbox-inner">
                  <span class="pdf-signature-text">Sign</span>
                </span>
              `
            }

            btn.addEventListener('click', (e) => {
              e.preventDefault()
              e.stopPropagation()
              onClickSignatureRef.current?.(sig.elementId)
            })

            overlayLayer.appendChild(btn)
          }

          const renderContext: RenderParameters = {
            canvasContext: ctx,
            viewport,
            canvas,
          }

          const task = page.render(renderContext)
          renderTasks.push(task)

          try {
            await task.promise
          } catch (err: any) {
            if (err?.name !== 'RenderingCancelledException') {
              throw err
            }
          }
        }

        if (!cancelled) {
          onRenderStatusChange?.(PdfRenderStatus.COMPLETE)
        }
      } catch (err) {
        if (!cancelled) {
          onRenderStatusChange?.(PdfRenderStatus.ERROR)
          Logger.errorObj('PDF render error', err)
          container.innerHTML = `<div style="padding:16px;">Failed to load PDF.</div>`
        }
      }
    }

    run()

    return () => {
      cancelled = true

      renderTasks.forEach((task) => {
        try {
          task.cancel?.()
        } catch (error) {
          Logger.errorObj('Error in PDFViewer', error)
        }
      })

      try {
        pdf?.destroy?.()
      } catch (error) {
        Logger.errorObj('Error in PDFViewer', error)
      }
    }
  }, [fileUrl, signatureFields, signatureMap, onRenderStatusChange, rerenderOnCustomInputs, zoomScale])

  return <div ref={containerRef} className={className} />
}
